import { jsx as _jsx } from "react/jsx-runtime";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import rehypeExternalLinks from 'rehype-external-links';
import styles from './Prose.module.css';
// Dev-mode detection that avoids referencing `process` globally (not in tsconfig types).
// Bundlers (Vite, Next.js, webpack) replace `process.env.NODE_ENV` at build time.
// We use Function constructor to defer the resolution past TypeScript's static analysis.
// eslint-disable-next-line @typescript-eslint/no-implied-eval
const DEV_MODE = (() => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-new-func
        return new Function('return typeof process!=="undefined"&&process.env.NODE_ENV==="development"')();
    }
    catch {
        return false;
    }
})();
// Heading clamp: markdown `#` maps to <h{startLevel}>, `##` to <h{startLevel+1}>, etc.
// Caps at h6.
function clampedLevel(markdownLevel, startLevel) {
    const level = markdownLevel - 1 + startLevel;
    return Math.min(level, 6);
}
// Sanitization schema: strict allowlist — no script, no iframe, no event handlers.
// Based on rehype-sanitize's defaultSchema, with explicit removals.
const sanitizeSchema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        // Allow target/rel on anchors (set by rehype-external-links) and alt/role on img.
        a: [...(defaultSchema.attributes?.['a'] ?? []), 'target', 'rel'],
        img: ['src', 'alt', 'title', 'width', 'height', 'role'],
        // rehype-slug puts an `id` on every heading so anchors work for TOC
        // linking. The allowlist must keep them — sanitize would otherwise
        // strip the id and break in-page navigation.
        h1: [...(defaultSchema.attributes?.['h1'] ?? []), 'id'],
        h2: [...(defaultSchema.attributes?.['h2'] ?? []), 'id'],
        h3: [...(defaultSchema.attributes?.['h3'] ?? []), 'id'],
        h4: [...(defaultSchema.attributes?.['h4'] ?? []), 'id'],
        h5: [...(defaultSchema.attributes?.['h5'] ?? []), 'id'],
        h6: [...(defaultSchema.attributes?.['h6'] ?? []), 'id'],
    },
    tagNames: (defaultSchema.tagNames ?? []).filter((tag) => tag !== 'script' && tag !== 'iframe' && tag !== 'object' && tag !== 'embed'),
};
// The `simple` variant's allowlist: paragraphs, lists, inline emphasis, and
// links only. Headings stay allowlisted because the variant renders them as
// paragraphs (see the components map) — sanitize runs before the components
// mapping, so stripping them here would unwrap their text as bare root text
// instead. Everything else (blockquote, code, tables, images, hr, …) is
// stripped by the sanitizer.
const simpleSanitizeSchema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        a: [...(defaultSchema.attributes?.['a'] ?? []), 'target', 'rel'],
    },
    tagNames: ['a', 'br', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'ol', 'p', 'strong', 'ul'],
};
const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
/**
 * Prose — scoped typography for markdown-rendered and ReactNode content.
 *
 * Source path (markdown string):
 *   <Prose source={markdownString} startLevel={2} />
 *
 * Children path (ReactNodes):
 *   <Prose>{reactNodes}</Prose>
 *
 * The markdown path uses `react-markdown` (single dep, SSR-safe, no pipeline
 * ceremony) with remark-gfm for tables/strikethrough/task-lists,
 * rehype-sanitize for strict allowlist XSS protection, and rehype-external-links
 * to auto-append target+rel on outbound links.
 *
 * Heading clamp: `startLevel` (default 2) maps markdown `#` to the given level.
 * Caps at h6.
 *
 * External links: get `target="_blank" rel="noopener noreferrer"` + a
 * visually-hidden SR suffix (configurable via `srExternalLinkSuffix`).
 *
 * Images: markdown `<img>` with missing alt renders as `alt="" role="presentation"`
 * and emits a dev-mode warning.
 *
 * Why react-markdown over unified+remark+rehype pipeline:
 *   - Single dependency vs 7 separate packages.
 *   - Ships its own SSR-safe React renderer; no custom serialise→dangerouslySetInnerHTML.
 *   - Accepts remark/rehype plugins natively — remark-gfm + rehype-sanitize +
 *     rehype-external-links all compose cleanly.
 *   - The unified pipeline would be necessary if we needed to produce HTML strings
 *     (e.g. email rendering), but for React output react-markdown is cleaner.
 */
export function Prose({ source, children, startLevel = 2, srExternalLinkSuffix = '', className, variant = 'document', typography = 'scale', breaks = false, allowRawHtml = false, remarkPlugins, components, extendSanitizeSchema, }) {
    const classes = [styles.prose, typography === 'inherit' ? styles.typeInherit : undefined, className]
        .filter(Boolean)
        .join(' ');
    // ReactNode children path: just apply the .prose typography scope.
    if (source === undefined) {
        return _jsx("div", { className: classes, children: children });
    }
    // Heading component factory with level clamping. Forwards `id` from
    // rehype-slug so anchor links work; drops other props rehype emits to
    // keep the rendered DOM clean.
    function makeHeading(markdownLevel) {
        const tag = `h${clampedLevel(markdownLevel, startLevel)}`;
        return function HeadingRenderer({ children: headingChildren, id, }) {
            const Tag = tag;
            return id !== undefined ? _jsx(Tag, { id: id, children: headingChildren }) : _jsx(Tag, { children: headingChildren });
        };
    }
    const baseSchema = variant === 'simple' ? simpleSanitizeSchema : sanitizeSchema;
    const schema = extendSanitizeSchema ? extendSanitizeSchema(baseSchema) : baseSchema;
    return (_jsx("div", { className: classes, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm, ...(breaks ? [remarkBreaks] : []), ...(remarkPlugins ?? [])], rehypePlugins: [
                ...(allowRawHtml ? [rehypeRaw] : []),
                ...(variant === 'document' ? [rehypeSlug] : []),
                [
                    rehypeExternalLinks,
                    {
                        target: '_blank',
                        rel: ['noopener', 'noreferrer'],
                        ...(srExternalLinkSuffix
                            ? {
                                content: { type: 'text', value: srExternalLinkSuffix },
                                contentProperties: { className: styles.srOnly, 'aria-hidden': 'false' },
                            }
                            : {}),
                    },
                ],
                [rehypeSanitize, schema],
            ], components: {
                h1: variant === 'simple' ? HeadingAsParagraph : makeHeading(1),
                h2: variant === 'simple' ? HeadingAsParagraph : makeHeading(2),
                h3: variant === 'simple' ? HeadingAsParagraph : makeHeading(3),
                h4: variant === 'simple' ? HeadingAsParagraph : makeHeading(4),
                h5: variant === 'simple' ? HeadingAsParagraph : makeHeading(5),
                h6: variant === 'simple' ? HeadingAsParagraph : makeHeading(6),
                // react-markdown passes the hast node to component overrides; it
                // must not spread onto the DOM element.
                img({ src, alt, node: _node, ...rest }) {
                    const resolvedAlt = alt ?? '';
                    if (!alt && DEV_MODE) {
                        // eslint-disable-next-line no-console
                        console.warn(`[Prose] <img src="${src}"> is missing an alt attribute. Rendering with alt="" role="presentation".`);
                    }
                    const extraProps = !alt ? { role: 'presentation' } : {};
                    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
                    return _jsx("img", { src: src, alt: resolvedAlt, ...extraProps, ...rest });
                },
                ...components,
            }, children: source }) }));
}
function HeadingAsParagraph({ children }) {
    return _jsx("p", { children: children });
}
//# sourceMappingURL=Prose.js.map